import React, { useContext, useEffect, useState } from "react";
import Navbar from "../components/Navbar/Navbar";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Footer from "../components/Public/Home/Footer/Footer";
import { CurrencyContext } from "../contexts/Currencycontext";
import { Helmet } from "react-helmet";

// Summer period: June 15 - August 31
const isSummerDate = (date) => {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  if (month === 6 && day >= 15) return true;
  if (month === 7) return true;
  if (month === 8 && day <= 31) return true;
  return false;
};

const LongueDuree = () => {
  const [isLoading, setisLoading] = useState(false);
  const navigate = useNavigate();
  const { carid } = useParams();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const pickupDateParam = urlParams.get("pickupDate");
  const dropoffDateParam = urlParams.get("dropoffDate");
  const pickupParam = urlParams.get("pickup");
  const dropoffParam = urlParams.get("dropoff");
  const category = urlParams.get("category");

  const isLongueDuree = category === "longueduree";

  const { currency, convertPrice } = useContext(CurrencyContext);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    address: "",
    phone: "",
    whatsapp: "",
    email: "",
    city: "",
    age: "",
    pickupLocation: pickupParam,
    dropoffLocation: dropoffParam,
    numVol: "",
    siegeAuto: false,
    paymentPercentage: 100,
  });

  const [carDetails, setCarDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCarDetails = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BASE_URL}cars/getcar/${carid}`);
        setCarDetails(response.data);
      } catch (err) {
        setError("Échec de la récupération des détails de la voiture");
      } finally {
        setLoading(false);
      }
    };
    fetchCarDetails();
  }, [carid]);

  useEffect(() => {
    if (carDetails?.title) {
      document.title = `Réservation - ${carDetails.title}`;
    }
  }, [carDetails]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "radio" ? value : type === "checkbox" ? checked : value,
    }));
  };

  // Get category data for the selected category
  const getCategoryData = () => {
    if (!carDetails) return null;
    if (carDetails.categories && carDetails.categories.length > 0) {
      const match = carDetails.categories.find((cat) => cat.type === category);
      return match || carDetails.categories[0];
    }
    return null;
  };

  // Calculate total with mixed pricing
  // Longue durée: price per MONTH (normalPrice/month, summerPrice/month)
  // Courte durée: price per DAY (normalPrice/day, summerPrice/day)
  const calculateTotal = () => {
    if (!pickupDateParam || !dropoffDateParam || !carDetails) return 0;

    const catData = getCategoryData();
    if (!catData) return 0;

    const normalPrice = catData.price;
    const summerPrice = catData.summerPrice;

    const start = new Date(pickupDateParam);
    const end = new Date(dropoffDateParam);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    if (totalDays <= 0) return 0;

    if (!summerPrice) {
      // No summer price — simple calculation
      if (isLongueDuree) {
        // Price per month: totalDays / 30 * monthPrice
        return (totalDays / 30) * normalPrice;
      } else {
        return totalDays * normalPrice;
      }
    }

    // Mixed calculation: count normal days vs summer days
    let normalDays = 0;
    let summerDays = 0;
    const current = new Date(start);
    while (current < end) {
      if (isSummerDate(current)) summerDays++;
      else normalDays++;
      current.setDate(current.getDate() + 1);
    }

    if (isLongueDuree) {
      // Longue durée: convert days to months (divide by 30)
      return (normalDays / 30) * normalPrice + (summerDays / 30) * summerPrice;
    } else {
      // Courte durée: per day
      return normalDays * normalPrice + summerDays * summerPrice;
    }
  };

  // Get price breakdown for display
  const getPriceBreakdown = () => {
    if (!pickupDateParam || !dropoffDateParam || !carDetails) return null;

    const catData = getCategoryData();
    if (!catData || !catData.summerPrice) return null;

    const normalPrice = catData.price;
    const summerPrice = catData.summerPrice;

    const start = new Date(pickupDateParam);
    const end = new Date(dropoffDateParam);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    let normalDays = 0;
    let summerDays = 0;
    const current = new Date(start);

    while (current < end) {
      if (isSummerDate(current)) summerDays++;
      else normalDays++;
      current.setDate(current.getDate() + 1);
    }

    if (summerDays === 0) return null;

    return { normalDays, summerDays, normalPrice, summerPrice };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setisLoading(true);

    const totalAmount = calculateTotal();
    const paidAmount = (totalAmount * formData.paymentPercentage) / 100;

    const data = {
      ...formData,
      startDate: pickupDateParam,
      endDate: dropoffDateParam,
      category: category,
      carModel: carid,
      totalPrice: totalAmount,
      paidAmount: paidAmount,
      paymentType: "onsite",
    };

    try {
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}rentings/create`, data);
      const resData = response.data;
      if (resData.redirectTo) {
        window.location.href = resData.redirectTo;
      } else {
        navigate("/confirmation");
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setisLoading(false);
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <div className='w-16 h-16 border-t-4 border-darkBlue rounded-full animate-spin'></div>
      </div>
    );
  }

  if (error) return <div>{error}</div>;

  const totalAmount = calculateTotal();
  const breakdown = getPriceBreakdown();
  const catData = getCategoryData();
  const priceUnit = isLongueDuree ? "Mois" : "Jour";

  return (
    <>
      <Helmet><title>Réservation</title></Helmet>
      <div className='relative'>
        <Navbar />
        <div className='mx-auto px-12 py-12 bg-grey'>
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>

            {/* Form Column */}
            <div className='bg-white p-6 rounded-lg shadow-lg'>
              <h2 className='text-2xl font-bold mb-4'>Informations personnelles</h2>
              <form onSubmit={handleSubmit}>
                <div className='flex flex-wrap -mx-2 mb-4'>
                  <div className='w-full md:w-1/2 px-2 mb-4 md:mb-0'>
                    <label className='block text-gray-700 mb-2'>Prénom</label>
                    <input type='text' name='firstName' value={formData.firstName} onChange={handleChange} placeholder='Entrez votre prénom' className='w-full border border-gray-300 rounded-md py-2 px-4' required />
                  </div>
                  <div className='w-full md:w-1/2 px-2'>
                    <label className='block text-gray-700 mb-2'>Nom</label>
                    <input type='text' name='lastName' value={formData.lastName} onChange={handleChange} placeholder='Entrez votre nom' className='w-full border border-gray-300 rounded-md py-2 px-4' required />
                  </div>
                </div>
                <div className='mb-4'>
                  <label className='block text-gray-700 mb-2'>Adresse</label>
                  <input type='text' name='address' value={formData.address} onChange={handleChange} placeholder='Entrez votre adresse' className='w-full border border-gray-300 rounded-md py-2 px-4' required />
                </div>
                <div className='mb-4'>
                  <label className='block text-gray-700 mb-2'>Téléphone</label>
                  <input type='text' name='phone' value={formData.phone} onChange={handleChange} placeholder='Entrez votre téléphone' className='w-full border border-gray-300 rounded-md py-2 px-4' required />
                </div>
                <div className='mb-4'>
                  <label className='block text-gray-700 mb-2'>Numéro WhatsApp</label>
                  <input type='text' name='whatsapp' value={formData.whatsapp} onChange={handleChange} placeholder='Entrez votre numéro WhatsApp' className='w-full border border-gray-300 rounded-md py-2 px-4' required />
                </div>
                <div className='mb-4'>
                  <label className='block text-gray-700 mb-2'>Email</label>
                  <input type='email' name='email' value={formData.email} onChange={handleChange} placeholder='Entrez votre email' className='w-full border border-gray-300 rounded-md py-2 px-4' required />
                </div>
                <div className='flex flex-wrap -mx-2 mb-4'>
                  <div className='w-full md:w-1/2 px-2 mb-4 md:mb-0'>
                    <label className='block text-gray-700 mb-2'>Ville</label>
                    <input type='text' name='city' value={formData.city} onChange={handleChange} placeholder='Entrez votre ville' className='w-full border border-gray-300 rounded-md py-2 px-4' required />
                  </div>
                  <div className='w-full md:w-1/2 px-2'>
                    <label className='block text-gray-700 mb-2'>Âge</label>
                    <input type='number' name='age' value={formData.age} onChange={handleChange} placeholder='Entrez votre âge' className='w-full border border-gray-300 rounded-md py-2 px-4' required />
                  </div>
                </div>
                <div className='mb-4'>
                  <label className='block text-gray-700 mb-2'>Numéro de vol (optionnel)</label>
                  <input type='text' name='numVol' value={formData.numVol} onChange={handleChange} placeholder='Entrez le numéro de vol' className='w-full border border-gray-300 rounded-md py-2 px-4' />
                </div>
                <div className='mb-4'>
                  <label className='block text-gray-700 font-semibold mb-2'>Avez-vous besoin de siège auto?</label>
                  <div className='flex items-center space-x-2'>
                    <input type='checkbox' name='siegeAuto' checked={formData.siegeAuto} onChange={handleChange} className='h-5 w-5 border border-gray-300 rounded-md' />
                    <span className='text-gray-600'>Oui, j'ai besoin d'un siège auto</span>
                  </div>
                </div>
                <div className='mb-4'>
                  <label className='block text-gray-700 font-semibold mb-2'>Pourcentage de paiement</label>
                  <select name='paymentPercentage' value={formData.paymentPercentage} onChange={handleChange} className='w-full border border-gray-300 rounded-md py-2 px-4' required>
                    <option value='30'>Payer 30% maintenant</option>
                    <option value='50'>Payer 50% maintenant</option>
                    <option value='100'>Payer 100% maintenant</option>
                  </select>
                </div>
                <button type='submit' className='bg-darkBlue text-white py-2 px-4 rounded-md'>
                  {isLoading ? "En cours..." : "Soumettre"}
                </button>
              </form>
            </div>

            {/* Summary Column */}
            <div>
              <div className='bg-white p-6 rounded-t-lg shadow-lg h-fit'>
                <h2 className='text-2xl font-bold mb-4 text-center'>Informations de location</h2>
                {carDetails && (
                  <>
                    <h2 className='text-lg font-bold mb-4'>{carDetails.title}</h2>
                    <div className='mt-4'>
                      <p>De : {new Date(pickupDateParam).toLocaleDateString("fr-FR")} {new Date(pickupDateParam).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</p>
                      <p className='font-semibold'>Lieu de départ : {pickupParam}</p>
                      <p>À : {new Date(dropoffDateParam).toLocaleDateString("fr-FR")} {new Date(dropoffDateParam).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</p>
                      <p className='font-semibold'>Lieu de retour : {dropoffParam}</p>
                      <img
                        className='object-fit aspect-ratio-16/9 w-full rounded-md mt-4 p-8'
                        src={process.env.REACT_APP_BASE_URL + "uploads/" + carDetails.images[0]}
                        alt={carDetails.title}
                      />
                      <div className='mt-4 space-y-1'>
                        <p>Prix : {convertPrice(catData?.price || 0)} {currency}/{priceUnit}</p>
                        {/* {catData?.summerPrice && (
                          <p className='text-yellow-600 font-medium'>🌞 Prix été : {convertPrice(catData.summerPrice)} {currency}/{priceUnit}</p>
                        )} */}
                        <p>Dépôt de garantie : {convertPrice(carDetails.garantie)} {currency}</p>
                      </div>

                      {/* Price breakdown if mixed */}
                      {/* {breakdown && (
                        <div className='mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm'>
                          <p className='font-semibold text-yellow-800 mb-2'>🌞 Calcul prix mixte:</p>
                          {breakdown.normalDays > 0 && (
                            isLongueDuree ? (
                              <p className='text-gray-700'>
                                {(breakdown.normalDays / 30).toFixed(2)} mois × {convertPrice(breakdown.normalPrice)} {currency} = {convertPrice((breakdown.normalDays / 30) * breakdown.normalPrice)} {currency}
                              </p>
                            ) : (
                              <p className='text-gray-700'>
                                {breakdown.normalDays} jour(s) × {convertPrice(breakdown.normalPrice)} {currency} = {convertPrice(breakdown.normalDays * breakdown.normalPrice)} {currency}
                              </p>
                            )
                          )}
                          {breakdown.summerDays > 0 && (
                            isLongueDuree ? (
                              <p className='text-yellow-700'>
                                {(breakdown.summerDays / 30).toFixed(2)} mois été × {convertPrice(breakdown.summerPrice)} {currency} = {convertPrice((breakdown.summerDays / 30) * breakdown.summerPrice)} {currency}
                              </p>
                            ) : (
                              <p className='text-yellow-700'>
                                {breakdown.summerDays} jour(s) été × {convertPrice(breakdown.summerPrice)} {currency} = {convertPrice(breakdown.summerDays * breakdown.summerPrice)} {currency}
                              </p>
                            )
                          )}
                        </div>
                      )} */}
                    </div>
                  </>
                )}
              </div>

              <div className='bg-darkBlue-dark text-white text-2xl rounded-lg p-4 mt-2'>
                Total : {convertPrice(totalAmount)} {currency}
              </div>
              <div className='bg-darkBlue text-white text-xl rounded-lg p-4 mt-2'>
                Montant à payer maintenant : {convertPrice((totalAmount * formData.paymentPercentage) / 100)} {currency} ({formData.paymentPercentage}%)
              </div>
            </div>

          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default LongueDuree;